import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Calling from "../../api/Calling";
import NotFound from "../../components/ul/NotFound";
import generateCaptcha from "../../components/captcha";
import {
  LuCreditCard,
  LuPlus,
  LuSquarePen,
  LuTrash2,
  LuCheck,
  LuX,
  LuIndianRupee,
  LuDownload,
} from "react-icons/lu";

const TYPES = ["upi", "paypal", "binance"];

// Default withdraw cards used to seed a fresh setup.
const DEFAULT_CARDS = [
  { w_type: "upi", w_amount: 1500 },
  { w_type: "upi", w_amount: 2500 },
  { w_type: "upi", w_amount: 5000 },
  { w_type: "paypal", w_amount: 100 },
  { w_type: "paypal", w_amount: 250 },
  { w_type: "paypal", w_amount: 500 },
  { w_type: "binance", w_amount: 100 },
  { w_type: "binance", w_amount: 250 },
  { w_type: "binance", w_amount: 500 },
];

const typeStyles = {
  upi: "bg-teal-500/15 text-brand-teal",
  paypal: "bg-blue-500/15 text-blue-600",
  binance: "bg-amber-500/15 text-amber-600",
};

const WithdrawCard = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ w_amount: "", w_type: "upi" });

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getWithdrawCard();
      if (resp?.status) setData(resp?.data || []);
      else toast.error(resp?.message);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? data : data.filter((c) => c.w_type === filter)),
    [data, filter]
  );

  const openAdd = () => {
    setEditingId(null);
    setForm({ w_amount: "", w_type: "upi" });
    setShowForm(true);
  };

  const openEdit = (card) => {
    setEditingId(card._id);
    setForm({ w_amount: card.w_amount, w_type: card.w_type });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (form.w_amount === "" || Number(form.w_amount) < 0) {
      return toast.error("Enter a valid amount");
    }
    if (!TYPES.includes(form.w_type)) {
      return toast.error("Select a valid type");
    }
    setLoader(true);
    try {
      const body = { w_amount: Number(form.w_amount), w_type: form.w_type };
      const resp = editingId
        ? await Calling.updateWithdrawCard(editingId, body)
        : await Calling.addWithdrawCard(body);
      if (resp?.status) {
        toast.success(resp?.message || "Saved successfully");
        setShowForm(false);
        setEditingId(null);
        setForm({ w_amount: "", w_type: "upi" });
        fetchData();
      } else {
        toast.error(resp?.message || "Failed to save");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const handleImportDefaults = async () => {
    // Destructive: wipes ALL existing cards and replaces them with the defaults.
    const warning =
      data.length > 0
        ? `⚠️ WARNING: This will DELETE ALL ${data.length} existing withdraw card(s) and replace them with the default set. This cannot be undone. To confirm, solve this math problem.`
        : "This will add the default withdraw cards. To confirm, solve this math problem.";
    const check = generateCaptcha(warning);
    if (!check) return toast.error("Cancelled — nothing changed");

    setLoader(true);
    try {
      // 1) Delete every existing card
      if (data.length > 0) {
        await Calling.deleteWithdrawCard({ id: data.map((c) => c._id) });
      }
      // 2) Add the default set
      let added = 0;
      for (const c of DEFAULT_CARDS) {
        const resp = await Calling.addWithdrawCard({
          w_amount: c.w_amount,
          w_type: c.w_type,
        });
        if (resp?.status) added++;
      }
      toast.success(`Reset to ${added} default card${added === 1 ? "" : "s"}`);
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error("Failed to reset to defaults");
    } finally {
      setLoader(false);
    }
  };

  const handleDelete = async (id) => {
    const check = generateCaptcha("You are about to delete this withdraw card.");
    if (!check) return toast.error("Cancelled");
    setLoader(true);
    try {
      const resp = await Calling.deleteWithdrawCard({ id: [id] });
      if (resp?.status) {
        toast.success(resp?.message || "Deleted");
        fetchData();
      } else {
        toast.error(resp?.message || "Failed to delete");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <div className="md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 pb-5">
          <div className="flex items-center gap-4">
            <span className="p-3.5 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl shadow-lg shadow-teal-500/30 flex items-center justify-center">
              <LuCreditCard className="text-2xl md:text-3xl" />
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-main-font tracking-tight leading-none">
                Withdraw Cards
              </h1>
              <p className="text-sm text-main-subfont mt-1.5 flex items-center gap-2">
                Manage withdrawal amount options for the app
                {loader && (
                  <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-brand-teal border-t-transparent animate-spin" />
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleImportDefaults}
              disabled={loader}
              title="Delete all existing cards and reset to the default UPI / PayPal / Binance amounts"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-main-border bg-white px-4 py-2.5 font-semibold text-main-font hover:border-brand-teal/50 hover:text-brand-teal hover:bg-teal-500/5 disabled:opacity-60 transition-all"
            >
              <LuDownload size={18} /> Import Defaults
            </button>
            <button
              onClick={openAdd}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-2.5 font-semibold text-white shadow-lg shadow-teal-500/30 hover:brightness-110 hover:-translate-y-0.5 active:translate-y-0 transition-all"
            >
              <LuPlus size={18} /> Add Card
            </button>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-2 pb-5 border-b border-main-border">
          {["all", ...TYPES].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`inline-flex h-9 items-center justify-center rounded-xl px-4 text-sm font-medium capitalize transition-all ${
                filter === t
                  ? "bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-md shadow-teal-500/30"
                  : "bg-white text-main-subfont border border-main-border hover:border-teal-400/60 hover:text-brand-teal hover:bg-teal-500/5"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Add / Edit form */}
        {showForm && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <div
              className="ui-card-solid w-full max-w-lg p-6 ui-rise"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-main-font">
                  {editingId ? "Edit Withdraw Card" : "Add Withdraw Card"}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-main-subfont hover:text-main-font hover:bg-main-bg transition-colors"
                >
                  <LuX size={18} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wide text-main-subfont mb-1.5">
                  Amount (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.w_amount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, w_amount: e.target.value }))
                  }
                  placeholder="Enter amount"
                  className="ui-input w-full px-3 py-2.5 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-main-subfont mb-1.5">
                  Type
                </label>
                <select
                  value={form.w_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, w_type: e.target.value }))
                  }
                  className="ui-input w-full px-3 py-2.5 rounded-xl cursor-pointer capitalize"
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t} className="capitalize">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-2.5 font-semibold text-white shadow-lg shadow-teal-500/30 hover:brightness-110 transition-all"
              >
                <LuCheck size={18} /> {editingId ? "Update" : "Create"}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="inline-flex items-center gap-2 rounded-xl border border-main-border bg-white px-5 py-2.5 font-medium text-main-subfont hover:text-main-font hover:bg-main-bg transition-all"
              >
                <LuX size={18} /> Cancel
              </button>
            </div>
            </div>
          </div>
        )}

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-6">
          {loader ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white border border-main-border shadow-sm p-5 animate-pulse"
              >
                <div className="h-6 w-24 bg-black/10 rounded mb-6" />
                <div className="flex gap-2">
                  <div className="h-9 flex-1 bg-black/10 rounded-lg" />
                  <div className="h-9 flex-1 bg-black/10 rounded-lg" />
                </div>
              </div>
            ))
          ) : filtered.length > 0 ? (
            filtered.map((card) => (
              <div
                key={card._id}
                className="ui-card-solid p-5 hover:border-brand-teal/30 transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="flex items-center text-2xl font-extrabold text-brand-teal">
                    <LuIndianRupee size={20} className="mt-0.5" />
                    {card.w_amount}
                  </p>
                  <span
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold capitalize ${
                      typeStyles[card.w_type] || "bg-black/5 text-main-subfont"
                    }`}
                  >
                    {card.w_type}
                  </span>
                </div>

                <div className="flex gap-2 mt-6">
                  <button
                    onClick={() => openEdit(card)}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 px-3 py-2 text-sm font-semibold text-white shadow-md shadow-teal-500/25 hover:brightness-110 transition-all"
                  >
                    <LuSquarePen size={16} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(card._id)}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-red-500/90 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500 transition-all"
                  >
                    <LuTrash2 size={16} /> Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full">
              <NotFound title="No withdraw cards yet" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WithdrawCard;
